/** @jsxImportSource ./simboluri-jsx */
// DESENUL ELEMENTULUI pentru coloana din dreapta a paginii de element:
// „view-ul cu elementul", peste care stă contextul în clădire (formatul de la
// ochiurile de placă, dus la toate tipurile).
//
// Nu desenează nimic nou: refolosește secțiunile pe care le au deja paginile
// pașilor (exportate din ele), alegând-o pe cea potrivită tipului. Singura
// excepție e AXUL de fundație, care n-avea desen — secțiunea lui (talpă +
// inimă) e mai jos.

import { useMemo } from "react";
import { t, t as tr, useT } from "../i18n";
import { Grinda3D, SectiuneTransversala } from "./StepArmareGrinzi";
import { SectiuneStalp, Stalp3D } from "./StepArmareStalpi";
import { Perete3D, SectiunePereteGrosime } from "./StepArmarePereti";
import { SectiunePlaca } from "./StepArmarePlaca";
import Ochi3D from "./Ochi3D";
import SectiuneLamela, { Lamela3D } from "./SectiuneLamela";
import { ArmareLamela, CroireAx, FemModel, FemPanou, GrindaArmata } from "./tipuri";
import { ElementIndexat } from "./elemente";
import { CotaH, CotaV, LinieTeren, mFmt } from "./cote";
import { FundatieStalp } from "./DesenStalpCotat";
import AxFundare3D from "./AxFundare3D";
import { AxArmat } from "./armareAx";

// Secțiunea unui ax de fundație: talpa + inima (detaliul „T" al practicii),
// cotată cu B, D_f, talpa și inima. Datele vin din `sectiune_fundatie`
// (Pasul 6) și din lățimea PROPRIE a axului — un ax lățit local are alt B
// decât cel global. Cotele folosesc primitivele comune; D_f e
// desenat de la linia terenului până la fața de jos a tălpii, nu presupus.
function SectiuneAx({
  B_m,
  s,
}: {
  B_m?: number;
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  s?: any;
}) {
  useT();
  const B = B_m ?? s?.B_m ?? 1.2;
  const hT = s?.h_talpa_m ?? 0.4;
  const bI = s?.b_inima_m ?? 0.4;
  const hI = s?.h_inima_m ?? 0.6;
  const Df: number = s?.Df_m ?? hT + hI;
  // UMPLUTURA de deasupra betonului: la detaliul «T» secțiunea umple șanțul
  // doar până la `h_peste` sub pardoseală, deci betonul NU începe la cota de
  // săpare. Fără ea, cota D_f scria 3,00 m peste un desen de 2,70 m —
  // aceeași categorie de greșeală cu cutia fixă de sub stâlp.
  const hp: number = s?.h_peste_m ?? Math.max(Df - hT - hI, 0);
  const W = 380;
  // aceeași scală pe ambele axe (proporțiile trebuie să rămână reale)
  const sc = Math.min(200 / Math.max(B, 0.1), 150 / Math.max(Df, 0.1));
  const bw = B * sc;
  const th = hT * sc;
  const iw = bI * sc;
  const ih = hI * sc;
  const ox = 122 + (200 - bw) / 2;
  const oyTeren = 30;
  const oyI = oyTeren + hp * sc;
  const oyT = oyI + ih;
  const H = oyT + th + 52;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: W }}>
      <LinieTeren x1={6} x2={W - 6} y={oyTeren} eticheta={t("struct.desen.cota_sapatura")} />
      {/* inima */}
      <rect x={ox + (bw - iw) / 2} y={oyI} width={iw} height={ih}
            fill="#ececec" stroke="#737373" strokeWidth={1.1} />
      {/* talpa */}
      <rect x={ox} y={oyT} width={bw} height={th}
            fill="#ececec" stroke="#737373" strokeWidth={1.1} />
      {/* COTELE */}
      <CotaH x1={ox} x2={ox + bw} y={oyT + th + 24} martorY={oyT + th}
             text={`B = ${mFmt(B)} m`} />
      <CotaV x={ox - 30} y1={oyTeren} y2={oyT + th} martorX={ox}
             text={`D_f = ${mFmt(Df)} m`} culoare="var(--acc)" />
      <CotaV x={ox + bw + 26} y1={oyI} y2={oyT + th} martorX={ox + bw}
             laDreapta text={`H_c ${mFmt(hT + hI)}`} size={9.5} />
      <CotaV x={ox + bw + 68} y1={oyT} y2={oyT + th} martorX={ox + bw}
             laDreapta text={t("struct.desen.talpa", { n: mFmt(hT) })} size={9} />
      {hp > 0.01 && (
        <CotaV x={ox - 72} y1={oyTeren} y2={oyI} martorX={ox + (bw - iw) / 2}
               text={t("struct.desen.umplutura", { n: mFmt(hp) })} size={9} />
      )}
      <CotaH x1={ox + (bw - iw) / 2} x2={ox + (bw + iw) / 2} y={oyI - 14}
             martorY={oyI} deasupra text={t("struct.desen.inima", { n: mFmt(bI) })} size={9.5} />
    </svg>
  );
}

// are elementul o vedere 3D PROPRIE? Pagina întreabă înainte să deseneze
// cardul, ca să nu rămână un card cu titlu și fără desen.
export function areVedere3D(
  el: ElementIndexat,
  panou?: FemPanou | null
): boolean {
  if (el.tip === "grinda" || el.tip === "perete") return !!el.brut?.armare;
  if (el.tip === "stalp") return !!el.brutParinte?.nivele;
  if (el.tip === "ochi") return !!(el.brut?.armare && panou);
  // AXUL de fundație are vedere: armătura lui nu e o singură carcasă
  // constantă, deci are ce arăta — barele
  // continue, cele suplimentare pe intervalele lor și etrierii la pasul lor.
  // Fără `armare` (rulare doar a Pasului 6) rămâne fără vedere, ca înainte.
  if (el.tip === "ax") return !!el.brut?.armare?.armare;
  // LAMELA are carcasa ei 3D (capetele, inima, etrierii), ca stâlpul
  if (el.tip === "lamela") return !!el.brut?.armare;
  return false;
}

/** Secțiunea REALĂ a unui ax armat (Pasul 18): B, H_c, talpa și inima ale
 *  axului ăstuia, nu ale secțiunii globale a Pasului 6 — un ax lățit local
 *  sau cu altă înălțime de grindă se desenează cum s-a armat. Fără Pasul 18
 *  rămâne secțiunea globală. */
/* eslint-disable @typescript-eslint/no-explicit-any */
export function sectiuneaAxului(el: ElementIndexat): any {
  const a = el.brut?.armare;
  if (!a || a.B_m == null || a.H_c_m == null) return el.param;
  const hT = a.h_talpa_m ?? el.param?.h_talpa_m ?? 0.4;
  const Df = a.Df_m ?? el.param?.Df_m ?? a.H_c_m;
  return {
    ...(el.param ?? {}),
    B_m: a.B_m,
    h_talpa_m: hT,
    b_inima_m: a.b_inima_m ?? el.param?.b_inima_m ?? 0.4,
    h_inima_m: Math.max(a.H_c_m - hT, 0),
    Df_m: Df,
    h_peste_m: Math.max(Df - a.H_c_m, 0),
  };
}
/* eslint-enable @typescript-eslint/no-explicit-any */

// VEDEREA 3D a elementului — cea din paginile pașilor (grinda cu armătura și
// placa activă, stiva stâlpului, panoul de perete, felia de placă a ochiului).
//
// Când elementul N-ARE vedere proprie, aici NU se pune clădirea aprinsă:
// cardul „Contextul în clădire" de dedesubt e exact acela, iar pagina ajungea
// să arate aceeași clădire de două ori și nicăieri piesa. Fără vedere
// proprie, componenta întoarce `null` și cardul dispare cu totul.
export function Vedere3DElement({
  el,
  model,
  panou,
  height = 320,
  totStalpul = false,
  totAxul = false,
  axGrinzi,
  croire,
  fundatie,
}: {
  el: ElementIndexat;
  model?: FemModel | null;
  // geometria ochiului de placă (contur, laturi, goluri) — vine din Pasul 7,
  // fiindcă Pasul 16 ține cifrele, nu conturul
  panou?: FemPanou | null;
  height?: number;
  // la stâlpi: carcasa se restrânge la NIVELUL elementului, dacă omul nu
  // cere stiva întreagă („doar bucata selectată")
  totStalpul?: boolean;
  // la grinzi: axul coliniar întreg, dacă omul îl cere (aceeași alegere ca la stâlpi: „doar bucata" / „toată piesa")
  totAxul?: boolean;
  axGrinzi?: GrindaArmata[];
  /** croiala barelor axului — desenează suprapunerile pe bare */
  croire?: CroireAx | null;
  fundatie?: FundatieStalp | null;
}) {
  useT();
  const p = el.param ?? {};
  // felia de niveluri trebuie să fie STABILĂ între randări: `Stalp3D` o are în
  // dependențele lui useEffect, iar un array nou de fiecare dată ar reconstrui
  // scena WebGL la orice re-randare a paginii
  const nivelSingur = useMemo(
    () => (el.tip === "stalp" && el.brut ? [el.brut] : undefined),
    [el.tip, el.brut]
  );
  try {
    if (el.tip === "grinda" && el.brut?.armare)
      return (
        <Grinda3D g={el.brut} ax={totAxul ? axGrinzi : undefined}
                  croire={croire} />
      );
    if (el.tip === "stalp" && el.brutParinte?.nivele)
      return (
        <Stalp3D
          s={el.brutParinte}
          cCm={Number(p.acoperire_cm ?? p.c_cm ?? 3)}
          nivele={totStalpul ? undefined : nivelSingur}
          fundatie={totStalpul ? fundatie ?? null : null}
        />
      );
    if (el.tip === "perete" && el.brut?.armare) return <Perete3D p={el.brut} />;
    if (el.tip === "ax" && el.brut?.armare?.armare)
      return <AxFundare3D ax={el.brut.armare as AxArmat} height={height} />;
    if (el.tip === "lamela" && el.brut?.armare)
      return (
        <Lamela3D l={el.brut as ArmareLamela} height={height}
                  cCm={Number(p.c_cm ?? p.acoperire_cm ?? 4) || 4} />
      );
    if (el.tip === "ochi" && el.brut?.armare && panou)
      return (
        <Ochi3D
          ochi={el.brut}
          panou={panou}
          theta={Number(model?.theta_rad ?? 0) || 0}
          acoperire_mm={Number(p.acoperire_placa_mm ?? p.acoperire_mm ?? 20) || 20}
          height={height}
        />
      );
  } catch {
    return (
      <div className="sg-muted" style={{ fontSize: 12 }}>
        {t("struct.dse.vederea_3d_poate")}
      </div>
    );
  }
  return null;
}

// SECȚIUNEA PLĂCII PE SOL: grosimea, plasa la poziția ei REALĂ (la mijloc
// când betonul simplu duce momentul, la fața de jos când nu-l duce) și
// stratul de balast pe care reazemă. Fără desenul ăsta, „unde stă plasa" —
// singurul lucru care se schimbă de la un caz la altul — rămânea un cuvânt
// în tabel.
export function SectiunePlacaPeSol({ el }: { el: ElementIndexat }) {
  const b = el.brut ?? {};
  const t = Number(b.t_m ?? 0.15);
  const pl = b.plasa ?? {};
  const inc = b.incovoiere ?? {};
  const laMijloc = pl.pozitie !== "jos";
  const W = 620, H = 250;
  const marjaX = 118, latime = W - 2 * marjaX;
  const scale = 90 / Math.max(t * 100, 1);          // cm -> px
  const zSus = 56;
  const zJos = zSus + t * 100 * scale;
  const balast = 10;                                 // cm, stratul desenat
  const zBalast = zJos + balast * scale;
  const phi = Number(pl.phi_mm ?? 8) / 10;
  const ac = 3;                                      // cm, acoperirea de jos
  const yPlasa = laMijloc
    ? (zSus + zJos) / 2
    : zJos - (ac + phi / 2) * scale;
  const nBare = Math.min(14, Math.max(4, Math.round(latime / 44)));
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="ap-svg"
           style={{ width: "100%", maxWidth: W }}>
        <rect x={marjaX} y={zSus} width={latime} height={zJos - zSus}
              fill="#ececec" stroke="#232323" strokeWidth="1" />
        <rect x={marjaX} y={zJos} width={latime} height={zBalast - zJos}
              fill="#e6ded1" stroke="#b9ab93" strokeWidth="0.8" />
        {Array.from({ length: nBare }, (_, i) => (
          <circle key={i} cx={marjaX + (latime * (i + 0.5)) / nBare} cy={yPlasa}
                  r={Math.max(2, (phi / 2) * scale)} fill="#b5323d" />
        ))}
        <CotaV x={marjaX - 26} y1={zSus} y2={zJos} martorX={marjaX}
               text={`t = ${(t * 100).toFixed(0)} cm`} />
        <CotaV x={W - marjaX + 22} y1={yPlasa} y2={zJos} martorX={W - marjaX}
               laDreapta culoare="#b5323d"
               text={laMijloc ? tr("struct.desen.la_mijloc") : `${ac} cm`} />
        <LinieTeren x1={marjaX} x2={W - marjaX} y={zBalast}
                    eticheta={tr("struct.desen.balast", { n: balast })} />
        <text x={marjaX} y={zSus - 12} fontSize="10" fill="#737373">
          {pl.text ?? tr("struct.desen.plasa")}
        </text>
        <text x={6} y={H - 8} fontSize="10" fill="#737373">
          {tr(laMijloc ? "struct.desen.plasa_mijloc" : "struct.desen.plasa_jos")}
          {inc.M_ed_knm_m
            ? tr("struct.desen.moment_nota", {
                med: inc.M_ed_knm_m,
                mrd: inc.M_rd_beton_simplu_knm_m,
              })
            : ""}
        </text>
      </svg>
    </div>
  );
}

export default function DesenElement({ el }: { el: ElementIndexat }) {
  useT();
  const p = el.param ?? {};
  try {
    if (el.tip === "grinda" && el.brut?.armare) {
      return (
        <div style={{ overflowX: "auto" }}>
          <SectiuneTransversala g={el.brut} unde="reazem" />
        </div>
      );
    }
    if (el.tip === "stalp" && el.brut?.armare) {
      const c = Number(p.acoperire_cm ?? p.c_cm ?? 3);
      return (
        <div style={{ overflowX: "auto" }}>
          <SectiuneStalp n={el.brut} cCm={c} />
        </div>
      );
    }
    if (el.tip === "perete" && el.brut?.armare) {
      const c = Number(p.acoperire_cm ?? 3);
      return (
        <div style={{ overflowX: "auto" }}>
          <SectiunePereteGrosime p={el.brut} acoperire={c} />
        </div>
      );
    }
    if (el.tip === "ochi" && el.brut?.armare) {
      const ac = Number(p.acoperire_placa_mm ?? p.acoperire_mm ?? 20);
      return <SectiunePlaca ochi={el.brut} acoperire_mm={ac} />;
    }
    if (el.tip === "ax") {
      return <SectiuneAx B_m={el.brut?.B_m} s={sectiuneaAxului(el)} />;
    }
    if (el.tip === "lamela" && el.brut?.armare) {
      return (
        <div style={{ overflowX: "auto" }}>
          <SectiuneLamela l={el.brut as ArmareLamela}
                          cCm={Number(p.c_cm ?? p.acoperire_cm ?? 4) || 4} />
        </div>
      );
    }
    if (el.tip === "placa_sol") {
      return <SectiunePlacaPeSol el={el} />;
    }
  } catch {
    // un desen care nu poate citi o înregistrare mai veche NU are voie să
    // arunce pagina elementului: cifrele și verificările rămân vizibile
    return (
      <div className="sg-muted" style={{ fontSize: 12 }}>
        {t("struct.dse.desenul_poate_reconstitu")}
      </div>
    );
  }
  return (
    <div className="sg-muted" style={{ fontSize: 12 }}>
      {t("struct.dse.fara_desen_tipul")}
    </div>
  );
}
